import { useState } from "react";
import { GOLD, NAVY } from "../styles/palette";
import { sb } from "../lib/supabase";
import { ACTIVE_THEME } from "../themes";

// Phase D.5 — operator-facing "Plan & Billing" card.
// Shows the current plan, compares the two options, and switches between them via
// set-operator-plan (24h cooldown + the real Stripe work). flat→percentage is a
// PERIOD-END downgrade: the operator keeps flat benefits until saas_plan_cancel_at,
// then the webhook flips them to percentage. They can cancel that anytime ("Keep flat").
const MS_24H = 24 * 60 * 60 * 1000;

const PLANS = {
  percentage: { label: "Percentage", price: "2%", sub: "of client payments", blurb: "No monthly fee — you only pay as you earn." },
  flat:       { label: "Flat monthly", price: "$99", sub: "per month",        blurb: "We take 0% of client payments — predictable cost." },
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export function PlanBillingCard({ org, showToast, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const current = org?.saas_plan === "flat" ? "flat" : "percentage";
  const other   = current === "flat" ? "percentage" : "flat";
  const pending = !!org?.saas_plan_cancel_at; // flat now, scheduled to drop to percentage

  // 24h cooldown (mirrors the server guard so we can disable + explain up front).
  const changedAt = org?.plan_changed_at ? new Date(org.plan_changed_at).getTime() : 0;
  const sinceChange = changedAt ? Date.now() - changedAt : Infinity;
  const cooling = sinceChange < MS_24H;
  const hrsLeft = cooling ? Math.ceil((MS_24H - sinceChange) / (60 * 60 * 1000)) : 0;
  const notConnected = other === "flat" && !org?.stripe_account_id;
  const needsCard    = other === "flat" && !notConnected && !org?.platform_pm_id;

  async function callPlan(plan, confirmMsg) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const r = await sb.fn("set-operator-plan", { plan });
      if (r.error) { showToast(r.error, "error"); setBusy(false); return; }
      showToast(r.message || "Plan updated.");
      await onRefresh();
    } catch (e) {
      console.error(e);
      showToast("Could not update your plan — please try again.", "error");
    }
    setBusy(false);
  }

  const switchPlan = () => callPlan(other, other === "flat"
    ? `Switch to the Flat plan? You'll be charged $99/month starting today, and we'll stop taking the 2% fee on your client payments.`
    : `Switch to the Percentage plan at the end of your current billing period? You'll keep flat benefits (0% fee) until then — no refund for the current month.`);

  const keepFlat = () => callPlan("flat"); // undo a scheduled downgrade (no confirm needed)

  return (
    <div className="card">
      <div className="card-title">🧾 Plan &amp; Billing</div>
      <div style={{ fontSize: 12, color: "#8993a4", marginBottom: 14 }}>
        How you pay for iScoopCRM. Switch anytime — up to once every 24 hours.
      </div>

      {ACTIVE_THEME === "apple-native" ? (
        <div style={{ display: "flex", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
          {["percentage", "flat"].map(key => {
            const p = PLANS[key];
            const active = current === key;
            return (
              <div key={key} style={{
                flex: "1 1 200px", position: "relative", overflow: "hidden", borderRadius: 20, padding: "22px 22px 20px",
                color: active ? "#fff" : "var(--text)",
                background: active ? "linear-gradient(150deg, #0B3DA8 0%, #0A84FF 100%)" : "var(--surface)",
                border: active ? "none" : "1px solid var(--border)",
                boxShadow: active ? "0 20px 44px -14px rgba(10,90,210,.55)" : "var(--shadow-sm)",
                transition: ".2s var(--ease)",
              }}>
                {active && <div style={{ position: "absolute", top: -70, right: -50, width: 190, height: 190, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,.25), transparent 70%)", pointerEvents: "none" }} />}
                <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: ".02em", textTransform: "uppercase", color: active ? "rgba(255,255,255,.85)" : "var(--text2)" }}>{p.label}</span>
                  {active && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".05em", color: "#0B3DA8", background: "#fff", padding: "3px 10px", borderRadius: 20 }}>CURRENT</span>}
                </div>
                <div style={{ position: "relative", display: "flex", alignItems: "baseline", gap: 7 }}>
                  <span style={{ fontSize: 42, fontWeight: 700, letterSpacing: "-.035em", lineHeight: 1 }}>{p.price}</span>
                  <span style={{ fontSize: 12.5, color: active ? "rgba(255,255,255,.8)" : "var(--text2)" }}>{p.sub}</span>
                </div>
                <div style={{ position: "relative", fontSize: 12.5, lineHeight: 1.5, marginTop: 10, color: active ? "rgba(255,255,255,.9)" : "var(--text2)" }}>{p.blurb}</div>
              </div>
            );
          })}
        </div>
      ) : (
      <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
        {["percentage", "flat"].map(key => {
          const p = PLANS[key];
          const active = current === key;
          return (
            <div key={key} style={{
              flex: 1, borderRadius: 10, padding: 14,
              border: `2px solid ${active ? GOLD : "#e3e8ef"}`, background: active ? "#fffdf5" : "#fff",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 700, color: NAVY, fontSize: 14 }}>{p.label}</span>
                {active && <span style={{ fontSize: 10, fontWeight: 700, color: "#006644", background: "#e3fcef", padding: "2px 8px", borderRadius: 10 }}>CURRENT</span>}
              </div>
              <div style={{ margin: "6px 0 2px" }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: NAVY }}>{p.price}</span>{" "}
                <span style={{ fontSize: 11, color: "#8993a4" }}>{p.sub}</span>
              </div>
              <div style={{ fontSize: 11, color: "#5e6c84" }}>{p.blurb}</div>
            </div>
          );
        })}
      </div>
      )}

      {pending ? (
        // Scheduled downgrade — show the date + let them keep flat.
        <div>
          <div style={{ fontSize: 12, color: "#974f0c", background: "#fffae6", padding: "10px 12px", borderRadius: 6, marginBottom: 10 }}>
            ⏳ Switching to the <strong>Percentage</strong> plan on <strong>{fmtDate(org.saas_plan_cancel_at)}</strong>. You keep flat benefits (0% client fee) until then.
          </div>
          <button className="btn btn-gold" disabled={busy} onClick={keepFlat}>
            {busy ? "Working…" : "Keep the flat plan"}
          </button>
        </div>
      ) : (
        <>
          <button className="btn btn-gold" disabled={busy || cooling || needsCard || notConnected} onClick={switchPlan}>
            {busy ? "Working…" : `Switch to the ${PLANS[other].label} plan →`}
          </button>
          {cooling && (
            <div style={{ fontSize: 11, color: "#974f0c", background: "#fffae6", padding: "8px 12px", borderRadius: 6, marginTop: 10 }}>
              ⏳ You changed plans recently — you can change again in about {hrsLeft} hour{hrsLeft === 1 ? "" : "s"}.
            </div>
          )}
          {!cooling && notConnected && (
            <div style={{ fontSize: 11, color: "#974f0c", background: "#fffae6", padding: "8px 12px", borderRadius: 6, marginTop: 10 }}>
              ⚠️ The flat plan needs a connected Stripe account first — set it up in <strong>Payments (Stripe)</strong> above. Otherwise you'd pay $99/mo without being able to bill customers.
            </div>
          )}
          {!cooling && needsCard && (
            <div style={{ fontSize: 11, color: "#974f0c", background: "#fffae6", padding: "8px 12px", borderRadius: 6, marginTop: 10 }}>
              ⚠️ The flat plan needs a card on file — add one in <strong>Payment method</strong> just below, then switch.
            </div>
          )}
        </>
      )}
    </div>
  );
}
